import { useEffect, useRef, useState } from 'preact/hooks';
import * as st from '../state/store';
import { BY_ID } from '../state/structures';
import { tier } from '../entitlements/tier';
import { viewerRef } from './viewerRef';
import { absoluteUrl } from './share';
import { RESOLUTIONS, allowedResolutions, exportImage, shortUrl, type Background, type ResolutionId } from '../export';
import { saveImage } from '../export/save';

const BACKGROUNDS: Array<{ id: Background; label: string }> = [
  { id: 'transparent', label: 'Transparent' },
  { id: 'light', label: 'Light' },
  { id: 'dark', label: 'Dark' },
];

/** Save a PNG of the current view (F20). */
export function ExportDialog() {
  const sel = st.selected.value;
  const entry = sel ? BY_ID.get(sel.id) : null;
  const allowed = allowedResolutions(tier.value);
  const [background, setBackground] = useState<Background>('light');
  const [resolution, setResolution] = useState<ResolutionId>('1080p');
  const [label, setLabel] = useState(true);
  const [status, setStatus] = useState<{ kind: 'idle' | 'working' | 'done' | 'error'; text?: string }>({ kind: 'idle' });
  const first = useRef<HTMLInputElement>(null);
  const close = () => (st.exportOpen.value = false);

  useEffect(() => first.current?.focus(), []);

  const run = async () => {
    const viewer = viewerRef.current;
    if (!viewer) return;
    setStatus({ kind: 'working' });
    // Let the "Saving…" state paint before the render blocks the main thread.
    await new Promise((r) => setTimeout(r, 30));
    try {
      const url = shortUrl(absoluteUrl('/'), entry?.id);
      const res = await exportImage(viewer, {
        resolution,
        background,
        label: entry && label ? { text: entry.title, id: entry.id, side: sel?.side } : null,
        url,
      });
      const how = await saveImage(res.blob, `marrow-${entry?.id ?? 'body'}-${res.width}x${res.height}.png`);
      const note = res.scaled ? ` This device can’t render ${RESOLUTIONS[resolution].label}, so it was saved at ${res.width}×${res.height}, the largest it supports.` : '';
      setStatus({ kind: 'done', text: `${how === 'shared' ? 'Shared' : 'Saved'} ${res.width}×${res.height} image.${note}` });
    } catch {
      setStatus({ kind: 'error', text: 'Couldn’t save the image. Try a smaller size.' });
    }
  };

  return (
    <div class="overlay" onClick={(e) => e.target === e.currentTarget && close()} onKeyDown={(e) => e.key === 'Escape' && (e.stopPropagation(), close())}>
      <div class="dialog export-dialog" role="dialog" aria-modal="true" aria-labelledby="export-title" data-testid="export-dialog">
        <div class="dialog-head">
          <h2 id="export-title">Save image</h2>
          <button type="button" class="btn ghost icon" aria-label="Close" onClick={close}>✕</button>
        </div>
        <fieldset>
          <legend>Background</legend>
          {BACKGROUNDS.map((b, i) => (
            <label key={b.id} class="opt">
              <input ref={i === 0 ? first : undefined} type="radio" name="export-bg" value={b.id} checked={background === b.id} onChange={() => setBackground(b.id)} data-testid={`export-bg-${b.id}`} />
              {b.label}
            </label>
          ))}
        </fieldset>
        <fieldset>
          <legend>Size</legend>
          {(Object.keys(RESOLUTIONS) as ResolutionId[]).map((id) => {
            const locked = !allowed.includes(id);
            return (
              <label key={id} class={`opt ${locked ? 'locked' : ''}`}>
                <input type="radio" name="export-size" value={id} checked={resolution === id} disabled={locked} onChange={() => setResolution(id)} data-testid={`export-size-${id}`} />
                {RESOLUTIONS[id].label}
                {locked && <span class="lock-tag">Supporter</span>}
              </label>
            );
          })}
          {allowed.length < 3 && (
            <button type="button" class="link" onClick={() => ((st.panel.value = 'unlock'), close())} data-testid="export-unlock">
              Unlock 2K and 4K
            </button>
          )}
        </fieldset>
        {entry && (
          <label class="opt">
            <input type="checkbox" checked={label} onChange={(e) => setLabel((e.target as HTMLInputElement).checked)} data-testid="export-label" />
            Add a label: {entry.title}
          </label>
        )}
        <p class="fine">Every image carries a small credit line. The 3D models are shared under CC BY-SA, which requires it.</p>
        <div class="dialog-actions">
          <button type="button" class="btn primary" onClick={run} disabled={status.kind === 'working'} data-testid="export-save">
            {status.kind === 'working' ? 'Saving…' : 'Save image'}
          </button>
        </div>
        <p class="status" role="status" aria-live="polite" data-testid="export-status">{status.text ?? ''}</p>
      </div>
    </div>
  );
}
